import React from "react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  ArrowRight,
  Play,
  Sparkles,
  Search,
  Shield,
  Globe,
  Check,
  X,
} from "lucide-react";
import { Link } from "react-router-dom";

export const HeroSection = () => {
  const useCases = [
    {
      icon: Search,
      title: "Find Your Brand Name",
      description: "AI-powered generation of memorable, brandable names",
    },
    {
      icon: Shield,
      title: "Trademark Research",
      description: "Avoid trademark infringement with comprehensive analysis",
    },
    {
      icon: Globe,
      title: "Establish Online Presence",
      description: "Check domain & social media username availability",
    },
  ];

  const brandExamples = [
    {
      name: "Stellar Dynamics",
      quality: "Excellent",
      qualityColor: "text-green-800 bg-green-100",
      trademarkRisk: "Low",
      trademarkColor: "text-green-800 bg-green-100",
      available: true,
    },
    {
      name: "Quantum Leap",
      quality: "Good",
      qualityColor: "text-cyan-700 bg-cyan-100",
      trademarkRisk: "Medium",
      trademarkColor: "text-yellow-700 bg-yellow-100",
      available: true,
    },
    {
      name: "Nova Spark",
      quality: "Decent",
      qualityColor: "text-gray-700 bg-gray-100",
      trademarkRisk: "High",
      trademarkColor: "text-red-700 bg-red-100",
      available: false,
    },
  ];

  return (
    <section className="relative py-10 lg:py-16 overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-10 left-10 w-72 h-72 bg-teal-200/30 rounded-full blur-3xl animate-float"></div>
        <div
          className="absolute bottom-10 right-10 w-96 h-96 bg-cyan-200/20 rounded-full blur-3xl animate-float"
          style={{ animationDelay: "1s" }}
        ></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Column - Content */}
          <div className="animate-fade-in">
            <Badge className="mb-6 bg-teal-100 text-teal-700 hover:bg-teal-200 border-teal-200">
              <Sparkles className="w-4 h-4 mr-1" />
              AI-Powered Brand Intelligence
            </Badge>

            <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              <span className="bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">
                Name Pilot
              </span>{" "}
              Finds Your Perfect{" "}
              <span className="bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">
                Brand Name
              </span>{" "}
              in Minutes
            </h1>

            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Name Pilot is an AI-powered brand name generator, trademark
              analysis, and domain research tool for founders, creatives, and IP
              attorneys. Find powerful brand names fast, match them with
              available domains and social media accounts, and run trademark
              risk analysis.
            </p>

            {/* Three Main Use Cases */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
              {useCases.map((useCase, index) => {
                const Icon = useCase.icon;
                return (
                  <div
                    key={index}
                    className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-teal-100 hover:shadow-md transition-all duration-300"
                  >
                    <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-lg flex items-center justify-center mb-3">
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="font-semibold text-gray-900 text-lg mb-1">
                      {useCase.title}
                    </h3>
                    <p className="text-base text-gray-600">
                      {useCase.description}
                    </p>
                  </div>
                );
              })}
            </div>

            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <Button
                size="lg"
                className="bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white px-8 py-3 text-lg"
              >
                Start Generating Names
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>

              <Button
                variant="outline"
                size="lg"
                className="border-teal-200 text-teal-700 hover:bg-teal-50 px-8 py-3 text-lg"
              >
                <Play className="mr-2 w-5 h-5" />
                Watch Demo
              </Button>
            </div>

            <div className="flex items-center space-x-8 text-xl font-bold text-gray-500">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span>No Credit Card Required to Start</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span>Free Trial Available</span>
              </div>
            </div>
          </div>

          {/* Right Column - Visual */}
          <div
            className="relative animate-fade-in"
            style={{ animationDelay: "0.3s" }}
          >
            <div className="relative">
              {/* Main Card */}
              <div className="bg-white rounded-2xl shadow-2xl border border-teal-100 p-8 transform rotate-3 hover:rotate-0 transition-transform duration-300">
                <div className="space-y-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl flex items-center justify-center">
                      <Sparkles className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        Brand Name Generation and Analysis
                      </h3>
                      <p className="text-sm text-gray-500">
                        Comprehensive brand evaluation
                      </p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    {/* Header Row */}
                    <div className="grid grid-cols-4 gap-2 text-xs font-semibold text-gray-600 pb-2 border-b border-gray-200">
                      <div className="text-center">Brand Name</div>
                      <div className="text-center">Quality</div>
                      <div className="text-center">Risk</div>
                      <div className="text-center">Available</div>
                    </div>

                    {/* Brand Examples */}
                    {brandExamples.map((brand, index) => (
                      <div
                        key={index}
                        className="grid grid-cols-4 gap-2 items-center py-2"
                      >
                        <div className="font-medium text-teal-800 text-sm text-center">
                          {brand.name}
                        </div>
                        <div className="flex justify-center">
                          <Badge
                            className={`text-xs ${brand.qualityColor} border-0`}
                          >
                            {brand.quality}
                          </Badge>
                        </div>
                        <div className="flex justify-center">
                          <Badge
                            className={`text-xs ${brand.trademarkColor} border-0`}
                          >
                            {brand.trademarkRisk}
                          </Badge>
                        </div>
                        <div className="flex justify-center">
                          {brand.available ? (
                            <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                              <Check className="w-4 h-4 text-white" />
                            </div>
                          ) : (
                            <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
                              <X className="w-4 h-4 text-white" />
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  <Link to="/report">
                    <Button className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 text-white hover:from-teal-700 hover:to-cyan-700">
                      View Full Report
                    </Button>
                  </Link>
                </div>
              </div>

              {/* Floating Elements */}
              <div className="absolute -top-4 -right-4 bg-white rounded-lg shadow-lg p-3 animate-float">
                <div className="text-xs text-gray-500 mb-1">
                  Domain Available
                </div>
                <div className="text-sm font-semibold text-green-600">
                  ✓ stellardynamics.com
                </div>
              </div>

              <div
                className="absolute -bottom-4 -left-4 bg-white rounded-lg shadow-lg p-3 animate-float"
                style={{ animationDelay: "2s" }}
              >
                <div className="text-xs text-gray-500 mb-1">
                  Trademark Status
                </div>
                <div className="text-sm font-semibold text-teal-600">
                  ✓ Low Risk
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
